import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, IconButton, Stack, TextField, Typography
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, FormatQuote as QuoteIcon } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import AppBarContext from 'context/AppBarContext';
import {
  MANAGE_MOTIVATIONAL_PHRASES, CREATE_MOTIVATIONAL_PHRASE, DELETE_MOTIVATIONAL_PHRASE
} from 'api/graphql/motivationalPhrases';

/**
 * Gestión de frases motivacionales (admin). Las que se agregan aquí se mezclan con las curadas
 * de OTRIX y rotan en el banner del progreso físico del cliente.
 */
function MotivationalPhrasesAdmin() {
  const { t } = useTranslation(['routes']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const title = t('motivationalPhrases', { ns: 'routes', defaultValue: 'Frases motivacionales' });

  const { data, loading, refetch } = useQuery(MANAGE_MOTIVATIONAL_PHRASES, { fetchPolicy: 'cache-and-network' });
  const [createPhrase, { loading: creating }] = useMutation(CREATE_MOTIVATIONAL_PHRASE);
  const [deletePhrase] = useMutation(DELETE_MOTIVATIONAL_PHRASE);

  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(title);
  }, [title, setAppBarButton, setAppBarTitle]);

  const phrases = data?.manageMotivationalPhrases ?? [];

  const handleAdd = async () => {
    const value = text.trim();
    if (!value) return;
    await createPhrase({ variables: { text: value, author: author.trim() || null } });
    setText('');
    setAuthor('');
    refetch();
  };

  const confirmDelete = async () => {
    if (deleteId == null) return;
    await deletePhrase({ variables: { id: deleteId } });
    setDeleteId(null);
    refetch();
  };

  return (
    <Box>
      <SEOPageTitle title={title} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Las frases que agregues se mostrarán (junto con las de OTRIX) en el banner de progreso físico de los clientes.
      </Typography>

      {/* Formulario para agregar */}
      <PremiumCard innerSx={{ p: { xs: 2.5, md: 3 } }} sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Nueva frase</Typography>
        <Stack spacing={1.5}>
          <TextField
            size="small" fullWidth multiline minRows={2} label="Frase" value={text}
            onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 240 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small" fullWidth label="Autor (opcional)" placeholder="Si lo dejas vacío, se atribuye a OTRIX"
              value={author} onChange={(e) => setAuthor(e.target.value)} inputProps={{ maxLength: 80 }}
            />
            <Button
              variant="contained" color="primary" startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={handleAdd} disabled={creating || !text.trim()}
              sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0, alignSelf: { sm: 'stretch' } }}>
              Agregar
            </Button>
          </Stack>
        </Stack>
      </PremiumCard>

      {/* Listado */}
      {loading && phrases.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <Stack spacing={1.25}>
          {phrases.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>
              Aún no has agregado frases. Las de OTRIX siguen mostrándose por defecto.
            </Typography>
          )}
          {phrases.map((p) => (
            <PremiumCard key={p.id} innerSx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <QuoteIcon sx={{ color: 'gold.main', mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontStyle: 'italic' }}>“{p.text}”</Typography>
                  <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 700 }}>— {p.author || 'OTRIX'}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setDeleteId(p.id)} sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </PremiumCard>
          ))}
        </Stack>
      )}

      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        count={1}
        title="Eliminar frase"
        message="¿Seguro que quieres eliminar esta frase motivacional?"
      />
    </Box>
  );
}

export default MotivationalPhrasesAdmin;
